import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gavel, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const STATUS = {
  served: { c: C.amber, t: 'CLOCK RUNNING' }, executed: { c: C.red, t: 'COVER ENDED' },
  withdrawn: { c: C.green, t: 'WITHDRAWN — STILL COVERED' },
};
const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

export function CancellationNoticesCard() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get('/cancellations/notices', { params: { limit: 25 } })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data: d } = await api.post('/cancellations/notices/run');
      toast.success(`${d.running || 0} notice(s) still running · ${d.withdrawn || 0} withdrawn · ${d.executed || 0} took effect`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'The clock could not run'); }
    setRunning(false);
  };

  if (!data) return null;
  const tally = data.tally || {};
  const statute = data.statute || {};
  const notices = data.notices || [];

  return (
    <Card className="rounded-md bg-white p-4" data-testid="cancellation-notices-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${tally.served ? C.amber : C.slate}` }}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Gavel className="w-4 h-4" style={{ color: tally.served ? C.amber : C.slate }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
          Cancellation Notices · s59 Insurance Contracts Act 1984
        </h3>
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={load} className="p-1 rounded hover:bg-slate-50" data-testid="cn-refresh" style={{ color: C.slate }}>
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {isAdmin && (
            <Button size="sm" onClick={run} disabled={running} className="text-xs rounded-sm h-7"
              style={{ background: C.navy }} data-testid="cn-run-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Run the clock now'}
            </Button>
          )}
        </div>
      </div>
      <p className="text-[11px] mb-2.5" style={{ color: C.slate }} data-testid="cn-statute-note">
        We cannot simply cancel. Every insurer ground is served as a written notice naming its section of the Act, and cover ends at
        <b> 4:00pm on the third business day</b> after the notice — on the insured's own State calendar, fourteen where the contract runs on s58.
        Nobody approves it and nobody has to remember it: the clock runs on every pass, and the notice is <b>withdrawn automatically</b> if the ground
        is cured — the premium arrives, or the ABN verifies — and the policy simply stays in force.
      </p>

      <div className="flex gap-5 flex-wrap mb-2.5">
        {[['served', 'clocks running'], ['withdrawn', 'cured, still covered'], ['executed', 'cover ended on notice']].map(([k, label]) => (
          <div key={k}>
            <p className="text-2xl font-bold" style={{ color: STATUS[k].c }} data-testid={`cn-tally-${k}`}>{tally[k] || 0}</p>
            <p className="text-[10px] uppercase" style={{ color: C.slate }}>{label}</p>
          </div>
        ))}
      </div>

      {notices.length > 0 ? (
        <table className="w-full text-xs" data-testid="cn-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Policy</th><th className="py-1 pr-2">Ground</th>
            <th className="py-1 pr-2">Served</th><th className="py-1 pr-2">Cover ends 4:00pm</th>
            <th className="py-1 pr-2">Left</th><th className="py-1">State</th>
          </tr></thead>
          <tbody>
            {notices.map((n) => (
              <tr key={n.notice_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
                data-testid={`cn-row-${n.policy_number}`}>
                <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                  <span className="font-mono text-[10px] mr-1" style={{ color: C.slate }}>{n.policy_number}</span>{n.business_name}
                </td>
                <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                  {n.ground_label}
                  <span className="ml-1 font-mono text-[10px]" style={{ color: C.blue }}>{n.statute}</span>
                </td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.slate }}>{String(n.notice_date || '').slice(0, 10)}</td>
                <td className="py-1.5 pr-2 text-[11px]" style={{ color: C.navy }}>{String(n.effective_at || '').slice(0, 10)}</td>
                <td className="py-1.5 pr-2" data-testid={`cn-countdown-${n.policy_number}`}>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                    style={{ background: (STATUS[n.status] || STATUS.served).c + '15', color: (STATUS[n.status] || STATUS.served).c }}>
                    {n.status === 'served'
                      ? `${n.business_days_left ?? 0} business day(s)`
                      : (STATUS[n.status] || STATUS.served).t}
                  </span>
                  {n.status === 'executed' && n.refund_amount > 0 && (
                    <span className="ml-1 text-[10px]" style={{ color: C.green }}>{money(n.refund_amount)} returned</span>
                  )}
                </td>
                <td className="py-1.5 text-[11px]" style={{ color: C.slate }}>{n.state}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="cn-empty">
          No cancellation notice has been served. Grounds the system may serve on its own:
          {' '}{Object.entries(statute.grounds || {}).map(([g, s]) => `${g.replace(/_/g, ' ')} (${s})`).join(' · ')}.
        </p>
      )}
    </Card>
  );
}
